import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _AbErrorBoundary;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { OmitFromIdentity } from "abreact/core/pagestate/PageStateApi";
import { AbInlineErrorNotification } from "abreact/feedback/AbInlineErrorNotification";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { PrimaryRouteRenderContext } from "abreact/incubator/ui/app/router/routeRenderContext/PrimaryRouteRenderContext";
import { BundleStateService } from "abweb/service/module/BundleStateService";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
const LOG = LogUtils.getLogger(__filename);
/**
 * Simple Error boundary implementation that will prevent parent controls from
 * being taken down by errors in children.  Use like this:
 * <AbErrorBoundary>
 *   <PeskyComponent1/>
 *   <PeskyComponent2/>
 * </AbErrorBoundary>
 *
 * <p>Modelled after similar ErrorBoundary component found in React 'componentDidCatch' documentation:
 * https://reactjs.org/docs/error-boundaries.html
 *
 */
export let AbErrorBoundary = observer(_class = OmitFromIdentity(_class = (_class2 = (_AbErrorBoundary = class AbErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "safeResources", void 0);
    _initializerDefineProperty(this, "error", _descriptor, this);
    _defineProperty(this, "destroyRouteChangeListener", void 0);
    _defineProperty(this, "clearError", action((/*e: kendo.RouterChangeEvent*/
    ) => {
      if (this.error != null) {
        this.error = null;
        // CAT-1118: only log error cleared if there is an actual error being cleared
        LOG.debug("Cleared current error");
      }
      if (this.destroyRouteChangeListener) {
        this.destroyRouteChangeListener();
        this.destroyRouteChangeListener = undefined;
      }
    }));
    _defineProperty(this, "onTryAgainClicked", action(() => {
      this.clearError();
    }));
    makeObservable(this);
    this.error = null;
    this.safeResources = AbErrorBoundary.calcSafeResources();
  }
  getContextWithDefault() {
    return this.context ?? PrimaryRouteRenderContext.SINGLETON;
  }

  /**
   * Ensure that AbErrorBoundary can safely be used before i18n services are fully initialized (as errors
   *  might need to be displayed before i18n service initialization is complete).
   */
  static calcSafeResources() {
    try {
      return {
        title: AbReactResources.errorBoundary_title(),
        detailsBtnLabel: AbReactResources.errorBoundary_details_buttonLabel(),
        tryRedrawBtnLabel: AbReactResources.errorBoundary_tryRedraw_buttonLabel(),
        tryRedrawBtnTooltip: AbReactResources.errorBoundary_tryRedraw_buttonTooltip()
      };
    } catch (e) {
      // Fallback if i18n service is not yet initialized enough to get above resources (should be rare)
      return {
        title: "Rendering Error",
        detailsBtnLabel: "Details...",
        tryRedrawBtnLabel: "Try to redraw",
        tryRedrawBtnTooltip: "Area could not be rendered successfully.  Click to attempt redraw."
      };
    }
  }
  // noinspection JSUnusedLocalSymbols
  componentDidCatch(error, errorInfo) {
    this.recordError(error);
    this.renderErrorDialog();
    if (this.props.onError) {
      try {
        this.props.onError(error);
      } catch (err) {
        LOG.error("Error while invoking onError callback: " + err.toString());
      }
    }
  }
  recordError(error) {
    if (this.error) {
      // Defensive, should never happen, but if it does this prevents listener leakage
      this.clearError();
    }
    runInAction(() => {
      this.error = error;
    });
    if (this.props.tryAgainOnRouteChange) {
      this.destroyRouteChangeListener = this.createRouteChangeListener();
    }
  }
  createRouteChangeListener() {
    // Detects route handler changes (aka MAJOR/MID route changes) initiated via either goto or url/href change
    const destroyRouteHandlerChangeListener = reaction(() => this.getContextWithDefault().currentRouteHandler, this.clearError);
    // Detects browser-initiated route changes where url changes, which can include minor route changes (same RH)
    window.addEventListener("hashchange", this.clearError, false); // detect minor changes where RH does not change
    // Note believe that clicking a 'goto' that is a minor route change (SAME route handler) will
    // continue to show same error for existing RH.  Use AbErrorBoundary 'redraw' to retry existing route
    // (clears error for existing route handler instance).
    //
    // Longer-term if 'minor route changes' via goto need to be better supported:
    //   RouteRenderContext could support 'routeChangeListener' api that would also fire on
    //   minor route changes.
    return () => {
      destroyRouteHandlerChangeListener();
      // removeEventListener must match same args as addEventListener above or have mem leak
      window.removeEventListener("hashchange", this.clearError, false);
    };
  }
  componentWillUnmount() {
    // ensure no memory leakage: destroys any existing route change listeners if they exist
    this.clearError();
  }

  // Can NOT invoke from render method as render sometimes gets called multiple times in one react rendering pass
  // (resulting in same error being reported multiple times within error popup dialog)
  renderErrorDialog() {
    // CAT-1024: In react development mode, the error propagates all the way to the last chance error handler
    // (which displays error in popup) in addition to invoking componentDidCatch.
    // In production, only goes to componentDidCatch, so manually invoke the last chance
    // error handler so that the user is displayed error information (and error is logged), and the popup
    // also aggregates other errors that may be on screen.
    // Note that 'reportError' will debounce same error instances, but alas in development the error received by
    // componentDidCatch is different from the one that propagates to the last chance handler by itself.
    if (this.error != null && BundleStateService.isProduction) {
      // log and render error in popup
      LogAndRenderErrorPipeline.pipeline.reportError(this.error);
    }
  }
  render() {
    return this.error == null
    // Normally, just render children
    ? this.props.children
    // else render 'safe' pink background so that error popup can display successfully
    : /*#__PURE__*/React.createElement(AbInlineErrorNotification, {
      message: this.safeResources.title,
      error: this.error,
      buttons: this.props.showRefreshButton ? [{
        icon: "absdk:refresh",
        tooltip: this.safeResources.tryRedrawBtnTooltip,
        onClick: this.onTryAgainClicked,
        dataTestId: "redrawBtn"
      }] : undefined
    });
  }
}, _defineProperty(_AbErrorBoundary, "contextType", /*#__PURE__*/React.createContext(undefined)), _AbErrorBoundary), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "error", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class2)) || _class) || _class;

/**
 * Helper function to wrap an error boundary around a functional component.
 */
export function withErrorBoundary(WrappedComponent, errorBoundaryProps = {}) {
  function WithErrorBoundary(props) {
    return /*#__PURE__*/React.createElement(AbErrorBoundary, errorBoundaryProps, /*#__PURE__*/React.createElement(WrappedComponent, props));
  }
  return WithErrorBoundary;
}
//# sourceMappingURL=AbErrorBoundary.js.map
